// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Divider } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    isDashboard = "isDashboard",
    displayOverviewMessage = "displayOverviewMessage",
}

interface DashboardRowProps extends WidgetProps {
    fields: {
        [fields.isDashboard]: boolean;
        [fields.displayOverviewMessage]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const xIsAuthenticated = session.isAuthenticated && !session.isGuest;
    const currentBillTo = getBillToState(state, session.billToId).value;
    return {
        firstName: session?.firstName,
        customerNumber: currentBillTo?.customerNumber,
        xIsAuthenticated,
        dashboardUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    };
};

type Props = DashboardRowProps & ReturnType<typeof mapStateToProps>;

export interface AccountOverviewStyles {
    wrapper?: InjectableCss;
    greetingTitle?: TypographyPresentationProps;
    customerNumberLabel?: TypographyPresentationProps;
    customerNumberValue?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    accountMessage?: TypographyPresentationProps;
}

export const accountOverviewStyles: AccountOverviewStyles = {};

const AccountOverview: React.FunctionComponent<Props> = ({
    firstName,
    customerNumber,
    xIsAuthenticated,
    dashboardUrl,
    fields,
}: Props) => {
    const styles = accountOverviewStyles;

    if (!xIsAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.greetingTitle} data-test-selector="AccountOverview_greeting">
                {siteMessage("Custom_Account_Overview_Greeting")}
                {firstName && `, ${firstName}`}
            </Typography>
            {!fields.isDashboard && customerNumber && (
                <Typography {...styles.customerNumberLabel} data-test-selector="AccountOverview_customerNumber">
                    {translate("Customer")}: <Typography {...styles.customerNumberValue}>#{customerNumber}</Typography>
                </Typography>
            )}
            {!fields.isDashboard && (
                <Link href={dashboardUrl} {...styles.link}>
                    {translate("Visit Dashboard")}
                </Link>
            )}
            {fields.isDashboard && (
                <>
                    {createWidgetElement("MyAccount/MyAccountHomepageSelector", {
                        fields: {},
                    })}
                    <Divider margin="13px 0 0 0" />
                </>
            )}
            <Typography
                {...styles.accountMessage}
                {...{ dashboard: fields.isDashboard, displayOverviewMessage: fields.displayOverviewMessage }}
            >
                {siteMessage("Custom_Account_Overview_Message")}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(AccountOverview),
    definition: {
        group: "XNGAGE_CUSTOM_WIDGET" as any,
        displayName: "Account Overview",
        fieldDefinitions: [
            {
                name: fields.isDashboard,
                displayName: "is Dashboard Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.displayOverviewMessage,
                displayName: "Display overview message",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
